import { useLocalStorageState } from 'ahooks';
import { useEffect, useState } from 'react';
import { NIL } from 'uuid';
import { eLeadbotState } from '../../pages/MessengerV2/Messenger.enum';
import { toChatModalLead } from '../AdvanceSearch/AdvanceSearchModal.functions';
import { useACLContext } from '../../providers/ACLProvider/ACL.hook';
import { useModalsContext } from '../../providers/ModalsProvider/Modals.context';
import { eChannelType } from '../../providers/SocketsProvider/Sockets.enums';
import { useSocketsContext } from '../../providers/SocketsProvider/Sockets.context';
import { useSubscriptionContext } from '../../providers/SubscriptionProvider/Subscription.context';
import { useUserContext } from '../../providers/UserProvider/User.context';
import { useWorkspaceContext } from '../../providers/WorkspaceProvider/Workspace.context';
import { ChatService } from '../Chat/Chat.service';
import { tFunnelInfo } from '../Chat/Chat.types';
import { canViewLeadChat, needsFunnelPrivacy } from './LeadChat.functions';

/**
 * - `checking`: resolving the lead and whether this viewer may see it.
 * - `ready`: mount the chat.
 * - `failed`: the lead could not be loaded (missing, or a backend error; bff-web
 *   does not tell them apart). Retryable.
 * - `forbidden`: the viewer cannot see this lead, or stopped being able to.
 * - `deleted`: the lead was deleted while the popup was open.
 */
export type tChatModalGateStatus = 'checking' | 'ready' | 'failed' | 'forbidden' | 'deleted';

type tLeadAccessFacts = {
  leadbotState: eLeadbotState | null | undefined;
  assigneeAuthId: string | null | undefined;
  funnelId: string | null | undefined;
};

/**
 * Decides whether the chat popup may mount the chat for the selected lead (AGI-761).
 *
 * Opening one lead is not filtered by viewer on the backend (bff-web asks
 * messaging without the user, and leads-service's single-lead read skips the
 * Seller scope), and the chat creates a WhatsApp Web chat when none exists. So
 * for a lead opened from a link (`accessUnverified`) the popup resolves it and
 * applies the list visibility rules before the chat mounts. A lead from a list,
 * search or socket event is already filtered for the viewer and mounts at once,
 * with no request. Every lead keeps those rules applied while the popup is open.
 */
export default function useChatModalGate() {
  const { currentWorkspace } = useWorkspaceContext();
  const { currentUserId, currentUserRole } = useUserContext();
  const { canUserAccessWhatsAppApi } = useACLContext();
  // The default channel derives from the subscription; deciding before it loads picks WhatsApp Web.
  const { hasSubscriptionData } = useSubscriptionContext();
  const { selectedLeadForChatModal, setSelectedLeadForChatModal } = useModalsContext();
  const { leadUpdatedEvent, leadDeletedEvent } = useSocketsContext();
  const [funnelData] = useLocalStorageState<tFunnelInfo[]>('funnelData', {
    defaultValue: [],
    listenStorageChange: true,
  });
  // The verdict names its lead: on a swap to another lead the old `ready` must
  // not mount the chat for the new one before it is checked.
  const [verdict, setVerdict] = useState<{ leadId?: string; status: tChatModalGateStatus }>({
    status: 'checking',
  });
  const [attempt, setAttempt] = useState(0);

  const leadId = selectedLeadForChatModal?.id;
  const needsCheck = !!selectedLeadForChatModal?.accessUnverified;
  const workspaceId = currentWorkspace?.id;
  const settled = verdict.leadId === leadId ? verdict.status : undefined;
  const status = settled ?? (needsCheck ? 'checking' : 'ready');
  const settle = (next: tChatModalGateStatus) => setVerdict({ leadId, status: next });

  async function canView(facts: tLeadAccessFacts, signal?: AbortSignal) {
    const viewer = { currentUserRole, currentUserId, ...facts };
    let funnelIsPublic: boolean | undefined;
    if (workspaceId && facts.funnelId && needsFunnelPrivacy(viewer)) {
      const cached = funnelData?.find(({ uuid }) => uuid === facts.funnelId);
      funnelIsPublic = cached
        ? cached.is_public
        : (await ChatService.getWorkspacesInfo(workspaceId, facts.funnelId, { signal }))?.is_public;
    }
    return canViewLeadChat({ ...viewer, funnelIsPublic });
  }

  useEffect(() => {
    if (!leadId || !workspaceId || !needsCheck || !hasSubscriptionData) return undefined;
    const controller = new AbortController();
    // Clear rather than store `checking`: a check cancelled mid-flight must not leave a
    // verdict behind; `status` derives `checking` while one is pending.
    setVerdict({ status: 'checking' });

    (async () => {
      try {
        const { data } = await ChatService.getLead(
          { workspaceId, leadId },
          { signal: controller.signal },
        );
        // bff-web can answer 200 with the zero lead and an error list.
        if (!data?.id || data.id === NIL) throw new Error('lead not found');
        const visible = await canView(
          {
            leadbotState: data.leadbotState,
            assigneeAuthId: data.assigneeAuthId,
            funnelId: data.pipeId,
          },
          controller.signal,
        );
        if (controller.signal.aborted) return;
        if (visible) {
          // A link without a channel gets the one the chat itself would open (Chat.hook's
          // defaultChannel) before mounting. Left empty, the chat's "Get Lead info" merge
          // fills it in after the first load and loads the chat a second time.
          const integration =
            data.chat?.integration ??
            (canUserAccessWhatsAppApi() ? eChannelType.WHATSAPP_API : eChannelType.WHATSAPP_WEB);
          setSelectedLeadForChatModal((prev) =>
            prev?.id === leadId && !prev.chat?.integration
              ? toChatModalLead({ ...prev, chat: { ...prev.chat, integration } })
              : prev,
          );
        }
        settle(visible ? 'ready' : 'forbidden');
      } catch {
        if (!controller.signal.aborted) settle('failed');
      }
    })();

    return () => controller.abort();
    // canView reads the latest viewer and funnel cache on purpose; the check
    // re-runs only for a new lead, workspace or retry.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadId, workspaceId, needsCheck, hasSubscriptionData, attempt]);

  leadDeletedEvent.useSubscription((event) => {
    if (leadId && event.response.includes(leadId)) settle('deleted');
  });

  leadUpdatedEvent.useSubscription((event) => {
    const lead = event.response;
    if (lead.uuid !== leadId) return;
    // One way only: a partial update frame omits these fields, so it can never
    // prove visibility back; reopening the lead re-runs the full check.
    canView({
      leadbotState: lead.leadbot_state,
      assigneeAuthId: lead.user?.auth_id,
      funnelId: lead.funnel?.uuid,
    })
      .then((visible) => {
        if (!visible) {
          // Never over a notice already showing for this lead (deleted, failed).
          setVerdict((prev) =>
            prev.leadId === lead.uuid && prev.status !== 'ready'
              ? prev
              : { leadId: lead.uuid, status: 'forbidden' },
          );
        }
      })
      .catch(() => undefined);
  });

  return { status, retry: () => setAttempt((n) => n + 1) };
}
